import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { triggerLightTap } from '@/utils/haptics';

interface NotionToggleProps {
  id: string;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export function NotionToggle({ id, title, children, defaultOpen = false, onToggle }: NotionToggleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    triggerLightTap();
    onToggle?.(newState);
  };

  return (
    <div className="w-full">
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-sm hover:bg-[rgba(255,255,255,0.05)] transition-colors text-left focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-[#191919] break-words"
        aria-expanded={isOpen}
        aria-controls={`toggle-content-${id}`}
      >
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 w-5 h-5 flex items-center justify-center overflow-visible"
        >
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-[#9B9A97]" />
          ) : (
            <ChevronRight className="w-5 h-5 text-[#9B9A97]" />
          )}
        </motion.div>
        <span className="text-white/90 font-medium flex-1">{title}</span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={`toggle-content-${id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-y-hidden overflow-x-visible"
          >
            <div className="pl-7 py-3 pr-4 text-[#9B9A97] overflow-visible">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
